import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { IoMdEye, IoMdEyeOff } from "react-icons/io";
import { MdEmail, MdLock } from "react-icons/md";
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { getLoginRequest } from '../../redux/slices/authSlice';
import AuthLayout from './AuthLayout';
import InputBox from '../common/InputBox';
import { fadeInDown, fadeIn, staggerContainer, fadeInUp, createFadeInWithDelay } from '../../utils/animations';

// TypeScript interfaces
interface LoginInfo {
  userEmail: string;
  userPassword: string;
}

const Login: React.FC = () => {
  const [loginInfo, setLoginInfo] = useState<LoginInfo>({
    userEmail: '',
    userPassword: '',
  });
  
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [message, setMessage] = useState<string | null>(null);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const status = queryParams.get('status');
  const error = queryParams.get('error');
  
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const loginState = useAppSelector(state => state.auth);

  useEffect(() => {
    if (location.state?.message) {
      setMessage(location.state.message);
      const timer = setTimeout(() => setMessage(null), 10000);
      return () => clearTimeout(timer);
    }
  }, [location.state]);

  useEffect(() => {
    sessionStorage.clear();
  }, []);
  
  useEffect(() => {
    if (loginState.data?.status) {
      // Navigate to dashboard or home after successful login
      navigate("/dashboard");
    }
  }, [loginState.data?.status, navigate]);

  const handleInputChange = (field: keyof LoginInfo) => (value: string) => {
    setLoginInfo((prevInfo) => ({ ...prevInfo, [field]: value }));
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!loginInfo.userEmail || !loginInfo.userPassword) {
      return;
    }

    const payload = {
      email: loginInfo.userEmail,
      password: loginInfo.userPassword
    };
    
    dispatch(getLoginRequest(payload));
  };

  return (
    <AuthLayout
      title="All your campaigns,"
      subtitle="Starting with one idea."
    >
      <div className="w-full max-w-md h-full flex items-center justify-center">
        <div className="backdrop-blur-xl border border-gray-700/50 rounded-2xl shadow-2xl p-6 sm:p-8" style={{ width: '100%' }}>
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
          >
          <motion.h2 
            className="text-white text-center text-lg font-medium mb-4"
            variants={fadeInDown}
          >
            Hello! Sign in to continue
          </motion.h2>
          
          <motion.div 
            className="w-full h-px bg-gradient-to-r from-transparent via-orange-500 to-transparent mb-6"
            variants={fadeIn}
          />
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <motion.div variants={fadeInUp}>
              <label className="block text-gray-300 font-light text-xs mb-2">
                Email 
              </label>
              <InputBox
                type="email"
                id="email"
                placeholder="name@restaurant.com"
                onChange={handleInputChange('userEmail')}
                value={loginInfo.userEmail}
                required={true}
                className="w-full bg-transparent border border-gray-700 px-4 py-2.5 pl-12 text-white placeholder:text-gray-500 focus:outline-none focus:border-orange-500 hover:border-orange-500 transition-colors duration-300"
                style={{ borderRadius: '34px' }}
                icon={<MdEmail size={20} />}
              />
            </motion.div>
            
            <motion.div variants={fadeInUp}>
              <div className="flex justify-between font-light items-center mb-2">
                <label className="block text-gray-300 text-xs">
                  Password
                </label>
                <button
                  type="button"
                  onClick={() => navigate('/forgot-password')}
                  className="text-orange-500 font-regular font-light text-[10px] cursor-pointer hover:text-orange-400 transition-colors"
                >
                  Forgot Password?
                </button>
              </div>
              <div className="relative">
                <InputBox
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  placeholder="Enter your password"
                  onChange={handleInputChange('userPassword')}
                  value={loginInfo.userPassword}
                  required={true}
                  className="w-full bg-transparent border border-gray-700 px-4 py-2.5 pl-12 pr-12 text-white placeholder:text-gray-500 focus:outline-none focus:border-orange-500 hover:border-orange-500 transition-colors duration-300"
                  style={{ borderRadius: '34px' }}
                  icon={<MdLock size={20} />}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-orange-500 hover:text-orange-400 transition-colors"
                >
                  {showPassword ? <IoMdEyeOff size={22} /> : <IoMdEye size={22} />}
                </button>
              </div>
            </motion.div>

            {status === 'success' && (
              <div className='flex items-center font-light gap-2 text-sm text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3'>
                <MdEmail /> Email verified successfully. Please login to continue.
              </div>
            )}
            
            {loginState?.error && (
              <div className='text-sm text-rose-400 bg-rose-500/10 border border-rose-500/30 rounded-lg p-3 text-center'>
                {typeof loginState.error === 'string' 
                  ? loginState.error 
                  : 'An error occurred'}
              </div>
            )}
            
            {error === 'access_denied' && (
              <div className='text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-center'>
                Link expired
              </div>
            )}
            
            {message && (
              <div className="text-sm text-orange-300 bg-orange-500/10 border border-orange-500/30 rounded-lg p-3 text-center">
                {message}
              </div>
            )}
            
            <motion.div className="flex justify-center" variants={fadeInUp}>
              <button
                type="submit"
                disabled={loginState?.loading}
                style={{ borderRadius: '34px' }}
                className={`px-12 py-2.5 font-normal transition-colors duration-300 ${
                  loginState?.loading 
                    ? 'bg-gray-600 text-gray-400 cursor-not-allowed' 
                    : 'bg-gradient-to-r from-orange-500 to-orange-600 text-white hover:from-orange-600 hover:to-orange-700'
                }`}
              >
                {loginState?.loading ? 'Signing In...' : 'Sign In'}
              </button>
            </motion.div>

            {/* Divider */}
            <motion.div className="flex items-center gap-4 py-2" variants={fadeInUp}>
              <div className="flex-1 h-px bg-gray-700"></div>
              <span className="text-xs text-gray-500">OR</span>
              <div className="flex-1 h-px bg-gray-700"></div>
            </motion.div>

            {/* Clover Logo */}
            <motion.div className="flex justify-center py-3" variants={fadeInUp}>
              <button
                type="button"
                onClick={() => navigate('/clover')}
                className=" focus:outline-none"
                title="Connect with Clover"
              >
                <img 
                  src="/assets/images/clover-logo.png" 
                  alt="Clover Logo" 
                  className="h-12 w-auto cursor-pointer"
                />
              </button>
            </motion.div>

            <motion.div className="text-center pt-4" variants={fadeInUp}>
              <p className="text-xs text-gray-400">
                Don't have an account?{' '}
                <button
                  type="button"
                  onClick={() => navigate('/signup')}
                  className="text-orange-500 hover:text-orange-400 font-semibold transition-colors hover:underline"
                >
                  Sign Up
                </button>
              </p>
            </motion.div>
          </form>
          </motion.div>
        </div>
      </div>
    </AuthLayout>
  );
};

export default React.memo(Login);
